import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { ConfirmDialog, Dialog } from "../../components/Dialog";
import { Alert, Button, SelectField, TextArea, TextField } from "../../components/ui";
import { useToast } from "../../components/Toast";
import { ApiError, api, errorMessage } from "../../lib/api";
import { useConfig } from "../../lib/config";
import { localDateTimeParts } from "../../lib/format";
import type { OrderDetail, OrderSummary } from "../orders/types";

function useOrderMutation(orderId: string) {
  const qc = useQueryClient();
  const { notify } = useToast();
  return async (fn: () => Promise<OrderDetail>, success: string): Promise<boolean> => {
    try {
      qc.setQueryData(["staff-order", orderId], await fn());
      await qc.invalidateQueries({ queryKey: ["staff-orders"] });
      notify(success);
      return true;
    } catch (err) {
      notify(errorMessage(err), "error");
      if (err instanceof ApiError && err.code === "stale_version") {
        await qc.invalidateQueries({ queryKey: ["staff-order", orderId] });
        await qc.invalidateQueries({ queryKey: ["staff-orders"] });
      }
      return false;
    }
  };
}

/** "Mark completed" for open orders, "Reopen" for completed ones. */
export function CompleteOrderButton({
  order,
}: {
  order: Pick<OrderSummary, "id" | "order_number" | "production_status" | "version">;
}) {
  const mutate = useOrderMutation(order.id);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const status = order.production_status;
  if (status === "cancelled") return null;
  const completing = status !== "completed";
  const run = async () => {
    setBusy(true);
    const ok = await mutate(
      () =>
        api.post(`/staff/orders/${order.id}/status`, {
          to_status: completing ? "completed" : "in_production",
          reason: completing ? null : reason,
          version: order.version,
        }),
      completing
        ? `Order ${order.order_number} marked completed.`
        : `Order ${order.order_number} reopened.`,
    );
    setBusy(false);
    if (ok) setOpen(false);
  };
  return (
    <>
      <Button
        variant={completing ? "primary" : "secondary"}
        className="print:hidden"
        onClick={() => {
          setReason("");
          setOpen(true);
        }}
      >
        {completing ? "Mark completed" : "Reopen"}
      </Button>
      {completing ? (
        <ConfirmDialog
          open={open}
          title={`Mark order ${order.order_number} completed?`}
          body={
            <p>
              It moves to Closed orders and the customer gets a “completed” email. Their print files
              will be deleted in 90 days.
            </p>
          }
          confirmLabel="Mark completed"
          busy={busy}
          onConfirm={run}
          onCancel={() => setOpen(false)}
        />
      ) : (
        <Dialog
          open={open}
          title={`Reopen order ${order.order_number}?`}
          onClose={() => setOpen(false)}
          footer={
            <>
              <Button variant="secondary" onClick={() => setOpen(false)}>
                Go back
              </Button>
              <Button busy={busy} disabled={!reason.trim()} onClick={run}>
                Reopen
              </Button>
            </>
          }
        >
          <p className="mb-3">
            The order moves back to Open orders (In Production) and file deletion is paused.
          </p>
          <TextArea
            label="Reason"
            required
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Dialog>
      )}
    </>
  );
}

type FieldDef = {
  key: string;
  label: string;
  type?: "number" | "select" | "textarea" | "date" | "email" | "tel";
  options?: string;
  full?: boolean;
};

const SECTIONS: { title: string; fields: FieldDef[] }[] = [
  {
    title: "Customer",
    fields: [
      { key: "customer_name", label: "Customer name" },
      { key: "customer_company_name", label: "Company name" },
      { key: "customer_phone", label: "Phone", type: "tel" },
      { key: "customer_email", label: "Email", type: "email" },
      { key: "customer_street", label: "Street address", full: true },
      { key: "customer_city", label: "City" },
      { key: "customer_state", label: "State" },
      { key: "customer_zip", label: "ZIP" },
    ],
  },
  {
    title: "Due date",
    fields: [
      { key: "due_date", label: "Due date", type: "date" },
      { key: "due_time", label: "Due time", type: "select", options: "due_times" },
    ],
  },
  {
    title: "Delivery",
    fields: [
      { key: "delivery_method", label: "Delivery", type: "select", options: "delivery_methods" },
      { key: "ship_recipient", label: "Ship to: recipient" },
      { key: "ship_street", label: "Ship to: street", full: true },
      { key: "ship_city", label: "Ship to: city" },
      { key: "ship_state", label: "Ship to: state" },
      { key: "ship_zip", label: "Ship to: ZIP" },
      { key: "ship_instructions", label: "Delivery instructions", full: true },
    ],
  },
  {
    title: "Order",
    fields: [
      { key: "project_name_number", label: "Project" },
      { key: "payment_method", label: "Payment", type: "select", options: "payment_methods" },
      { key: "department", label: "Department", type: "select", options: "departments" },
      { key: "print_size", label: "Size", type: "select", options: "print_sizes" },
      { key: "originals_count", label: "Originals", type: "number" },
      { key: "printed_sets", label: "Printed sets", type: "number" },
      { key: "binding", label: "Binding", type: "select", options: "bindings" },
      { key: "binding_other", label: "Binding details" },
      { key: "notes", label: "Notes", type: "textarea", full: true },
    ],
  },
];

/** Administrators can change every order detail, including the due date. */
export function AdminOrderEditor({ order, onDone }: { order: OrderDetail; onDone: () => void }) {
  const { data: config } = useConfig();
  const tz = config?.business_timezone ?? "UTC";
  const qc = useQueryClient();
  const { notify } = useToast();
  const [due_date, due_time] = localDateTimeParts(order.effective_due_at, tz);
  const record = order as unknown as Record<string, unknown>;
  const initial: Record<string, string> = { due_date, due_time };
  for (const s of SECTIONS)
    for (const f of s.fields)
      if (!(f.key in initial)) initial[f.key] = record[f.key] == null ? "" : String(record[f.key]);
  const [values, setValues] = useState(initial);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const changed = Object.keys(values).filter((k) => values[k] !== initial[k]);
  const save = async () => {
    const changes: Record<string, unknown> = {};
    for (const k of changed) {
      if (k === "due_date" || k === "due_time") continue;
      const def = SECTIONS.flatMap((s) => s.fields).find((f) => f.key === k);
      changes[k] = values[k] === "" ? null : def?.type === "number" ? Number(values[k]) : values[k];
    }
    if (changed.includes("due_date") || changed.includes("due_time")) {
      changes.due_date = values.due_date;
      changes.due_time = values.due_time;
    }
    setBusy(true);
    setError(null);
    setFieldErrors({});
    try {
      qc.setQueryData(
        ["staff-order", order.id],
        await api.patch<OrderDetail>(`/staff/orders/${order.id}`, {
          changes,
          reason,
          version: order.version,
        }),
      );
      await qc.invalidateQueries({ queryKey: ["staff-orders"] });
      notify("Order updated.");
      onDone();
    } catch (err) {
      setError(errorMessage(err));
      if (err instanceof ApiError)
        setFieldErrors(Object.fromEntries(err.fields.map((f) => [f.field, f.message])));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        save();
      }}
    >
      {error && <Alert tone="error">{error}</Alert>}
      {SECTIONS.map((s) => (
        <fieldset key={s.title} className="rounded-lg border border-line bg-surface p-5">
          <legend className="px-1 text-lg font-bold">{s.title}</legend>
          <div className="mt-2 grid items-start gap-4 sm:grid-cols-2">
            {s.fields.map((f) => {
              const common = {
                label: f.label,
                value: values[f.key] ?? "",
                error: fieldErrors[f.key],
                onChange: (e: { target: { value: string } }) =>
                  setValues((v) => ({ ...v, [f.key]: e.target.value })),
              };
              const el =
                f.type === "select" ? (
                  <SelectField
                    {...common}
                    options={config?.options[f.options as keyof typeof config.options] ?? []}
                  />
                ) : f.type === "textarea" ? (
                  <TextArea {...common} rows={3} />
                ) : (
                  <TextField {...common} type={f.type ?? "text"} />
                );
              return (
                <div key={f.key} className={f.full ? "sm:col-span-2" : undefined}>
                  {el}
                </div>
              );
            })}
          </div>
        </fieldset>
      ))}
      <TextField
        label="Reason for the change"
        hint="Saved in the order's edit history."
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      <div className="flex flex-wrap gap-3">
        <Button type="submit" busy={busy} disabled={changed.length === 0}>
          Save {changed.length || ""} change{changed.length === 1 ? "" : "s"}
        </Button>
        <Button variant="secondary" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
